'use client';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { GitBranch, Cloud, CreditCard, Mail, BarChart3, MessageCircle, ExternalLink } from 'lucide-react';

export default function GDPRSubprocessorsPage() {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const subprocessors = [
    {
      name: 'Amazon Web Services (AWS)',
      region: 'United States & Multiple Regions',
      purpose: 'Cloud infrastructure and data hosting',
      icon: Cloud,
      compliance: 'GDPR-compliant',
      link: 'https://aws.amazon.com/compliance/gdpr-center/',
    },
    {
      name: 'Cloudflare',
      region: 'United States & Global',
      purpose: 'Web security and traffic geo-steering',
      icon: Cloud,
      compliance: 'GDPR-compliant',
      link: 'https://www.cloudflare.com/trust-hub/gdpr/',
    },
    {
      name: 'Google Analytics',
      region: 'United States',
      purpose: 'Web analytics and performance tracking',
      icon: BarChart3,
      compliance: 'GDPR-compliant',
      link: 'https://support.google.com/analytics/answer/6004245?hl=en',
    },
    {
      name: 'Paddle',
      region: 'United States',
      purpose: 'Payment processing',
      icon: CreditCard,
      compliance: 'GDPR-compliant',
      link: 'https://www.paddle.com/legal/gdpr',
    },
    {
      name: 'Postmark',
      region: 'United States',
      purpose: 'Transactional email services',
      icon: Mail,
      compliance: 'GDPR-compliant',
      link: 'https://postmarkapp.com/eu-privacy',
    },
    {
      name: 'Lucky Orange',
      region: 'United States',
      purpose: 'Website analytics and user insights',
      icon: BarChart3,
      compliance: 'GDPR-compliant',
      link: 'https://help.luckyorange.com/article/218-security-overview',
    },
    {
      name: 'Tawk.to',
      region: 'United States',
      purpose: 'Live chat and customer support',
      icon: MessageCircle,
      compliance: 'GDPR-compliant',
      link: 'https://www.tawk.to/data-protection/gdpr-2/',
    },
  ];

  const regionGroups = {
    'United States': subprocessors.filter(sp => sp.region.includes('United States')),
    'Global/Multiple Regions': subprocessors.filter(sp => sp.region.includes('Global') || sp.region.includes('Multiple')),
  };

  return (
    <div className="min-h-screen flex flex-col font-sans overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-accent/50 via-purple-100/30 to-background dark:from-purple-900/20 dark:via-purple-900/5 dark:to-background -z-10"></div>

      {/* Decorative elements */}
      <div className="absolute top-20 right-[10%] w-64 h-64 rounded-full bg-purple-200/20 dark:bg-purple-800/20 blur-3xl -z-10"></div>
      <div className="absolute bottom-20 left-[10%] w-96 h-96 rounded-full bg-indigo-200/20 dark:bg-indigo-800/20 blur-3xl -z-10"></div>

      <Navbar />

      <main className="flex-grow pt-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-12 text-center">
            <div className="inline-flex justify-center items-center w-16 h-16 rounded-full bg-purple-600/20 mb-4">
              <GitBranch className="h-8 w-8 text-purple-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">Sub-Processors</h1>
            <p className="text-lg text-muted-foreground">
              Transparency about the service providers we use to deliver PlotsAlot
            </p>
          </motion.div>

          {/* Introduction */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="bg-card/50 backdrop-blur-sm rounded-xl p-8 border border-border shadow-xl mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-4">Our Commitment to Transparency</h2>
            <p className="text-muted-foreground mb-4">
              PlotsAlot uses trusted third-party sub-processors to provide our services effectively and securely. All sub-processors are subject to GDPR-compliant data processing agreements (DPA) that ensure personal data is protected with the same high standards we maintain.
            </p>
            <p className="text-muted-foreground mb-4">
              We carefully select and vet each sub-processor for their commitment to data protection, security practices, and compliance with international data protection regulations. This list is current as of {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}.
            </p>
            <p className="text-muted-foreground">
              If you have concerns about any of our sub-processors or their data protection practices, please contact us at{' '}
              <a href="mailto:support@slashml.com" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold">
                support@slashml.com
              </a>
              .
            </p>
          </motion.div>

          {/* Sub-processors list */}
          <motion.div
            className="space-y-8"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}>
            {/* United States */}
            <motion.div variants={fadeIn}>
              <h2 className="text-2xl font-bold text-foreground mb-6">United States</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {regionGroups['United States'].map((sp, idx) => {
                  const Icon = sp.icon;
                  return (
                    <motion.div
                      key={idx}
                      variants={fadeIn}
                      className="p-6 rounded-lg bg-gradient-to-br from-blue-50/50 to-blue-100/30 dark:from-blue-950/30 dark:to-blue-900/20 border border-blue-200/50 dark:border-blue-900/50 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <Icon className="h-8 w-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded-full">
                          {sp.compliance}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">{sp.name}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{sp.purpose}</p>
                      <p className="text-xs text-muted-foreground mb-4">
                        <span className="font-semibold">Region:</span> {sp.region}
                      </p>
                      <a
                        href={sp.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm font-semibold group">
                        GDPR Details
                        <ExternalLink className="w-3 h-3 ml-2 transition-transform group-hover:translate-x-0.5" />
                      </a>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>

            {/* Global/Multiple Regions */}
            <motion.div variants={fadeIn}>
              <h2 className="text-2xl font-bold text-foreground mb-6">Global & Multiple Regions</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {regionGroups['Global/Multiple Regions'].map((sp, idx) => {
                  const Icon = sp.icon;
                  return (
                    <motion.div
                      key={idx}
                      variants={fadeIn}
                      className="p-6 rounded-lg bg-gradient-to-br from-purple-50/50 to-purple-100/30 dark:from-purple-950/30 dark:to-purple-900/20 border border-purple-200/50 dark:border-purple-900/50 hover:shadow-lg transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <Icon className="h-8 w-8 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded-full">
                          {sp.compliance}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">{sp.name}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{sp.purpose}</p>
                      <p className="text-xs text-muted-foreground mb-4">
                        <span className="font-semibold">Region:</span> {sp.region}
                      </p>
                      <a
                        href={sp.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 text-sm font-semibold group">
                        GDPR Details
                        <ExternalLink className="w-3 h-3 ml-2 transition-transform group-hover:translate-x-0.5" />
                      </a>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>

          {/* Data Processing Agreements */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="mt-12 bg-gradient-to-br from-amber-50/50 to-amber-100/30 dark:from-amber-950/30 dark:to-amber-900/20 rounded-xl p-8 border border-amber-200/50 dark:border-amber-900/50">
            <h2 className="text-2xl font-bold text-foreground mb-4">Data Processing Agreements (DPA)</h2>
            <p className="text-muted-foreground mb-4">
              PlotsAlot has entered into GDPR-compliant Data Processing Agreements (DPA) with each sub-processor. These agreements:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside mb-4">
              <li>Specify the subject matter and duration of processing</li>
              <li>Outline the nature and purpose of processing</li>
              <li>Define the types of personal data and categories of data subjects</li>
              <li>Require appropriate technical and organizational security measures</li>
              <li>Restrict further sub-processing to only those approved by us</li>
              <li>Include provisions for audit rights and data subject rights requests</li>
              <li>Ensure data is processed only on instructions from PlotsAlot</li>
            </ul>
            <p className="text-muted-foreground">
              All sub-processors are required to maintain the same level of data protection as PlotsAlot itself.
            </p>
          </motion.div>

          {/* Data Transfers */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="mt-12 bg-gradient-to-br from-indigo-50/50 to-indigo-100/30 dark:from-indigo-950/30 dark:to-indigo-900/20 rounded-xl p-8 border border-indigo-200/50 dark:border-indigo-900/50">
            <h2 className="text-2xl font-bold text-foreground mb-4">International Data Transfers</h2>
            <p className="text-muted-foreground mb-4">
              Some of our sub-processors are located in countries outside the European Economic Area (EEA), including the United States. For such transfers, we implement appropriate safeguards as required by GDPR, including:
            </p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside mb-4">
              <li>Standard Contractual Clauses (SCC)</li>
              <li>Binding Corporate Rules (BCR)</li>
              <li>Adequacy Decisions (where applicable)</li>
              <li>Supplementary safeguards and technical measures</li>
            </ul>
            <p className="text-muted-foreground">
              These mechanisms ensure your personal data is protected at the same level as required within the EEA.
            </p>
          </motion.div>

          {/* Changes notification */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="mt-12 text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">Updates to This List</h2>
            <p className="text-muted-foreground mb-6">
              We update this sub-processor list regularly as our third-party service providers change. If you would like to be notified of changes to sub-processors, please contact us at{' '}
              <a href="mailto:support@slashml.com" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold">
                support@slashml.com
              </a>
              .
            </p>
          </motion.div>

          {/* Back to GDPR */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="mt-12 text-center">
            <p className="text-muted-foreground mb-6">
              For more information about our GDPR compliance practices, visit our{' '}
              <a href="/gdpr-compliance" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-semibold">
                GDPR Compliance page
              </a>
              .
            </p>
          </motion.div>

          {/* Last updated */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-8 text-center text-sm text-muted-foreground">
            <p>Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
